// excelParser.js
import * as XLSX from 'xlsx';

// 检测并解码文件内容（支持 UTF-8 / GBK）
async function decodeFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const buffer = e.target.result;

        let text;
        try {
          const decoder = new TextDecoder('utf-8');
          text = decoder.decode(buffer);
          // 简单判断是否乱码（可选）
          if (text.includes('锘') || /[\u0080-\uFFFF]{3,}/.test(text)) {
            throw new Error('疑似乱码');
          }
        } catch (utf8Err) {
          try {
            const gbkDecoder = new TextDecoder('gbk', { fatal: false });
            text = gbkDecoder.decode(buffer);
          } catch (gbkErr) {
            throw new Error('不支持的文件编码，仅支持 UTF-8 或 GBK');
          }
        }

        resolve(text);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsArrayBuffer(file);
  });
}

// 🧠 智能清洗单元格值：去除首尾空格和引号，保留数字/布尔类型
const smartCleanCell = (value) => {
  if (value === null || value === undefined) return '';

  // ✅ 如果是 Date 对象，格式化为 "YYYY-MM-DD HH:mm:ss"
  if (value instanceof Date) {
    const pad = (n) => n.toString().padStart(2, '0');
    const year = value.getFullYear();
    const month = pad(value.getMonth() + 1); // 月份从0开始
    const day = pad(value.getDate());
    const hours = pad(value.getHours());
    const minutes = pad(value.getMinutes());
    const seconds = pad(value.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // 如果是数字或布尔值，直接返回（不处理）
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  // 如果是字符串，清洗引号和空格
  let str = String(value).trim().replace(/^['"]+|['"]+$/g, '');

  // 尝试转为数字
  const num = Number(str);
  if (!isNaN(num) && str !== '') {
    // 避免把前导零编号（如 "001", "07"）错误转成数字
    if (str.startsWith('0') && str.length > 1) {
      return str; // 保留为字符串
    }
    // 如果转换后等于原字符串（如 "25.5" → "25.5"），说明是有效数字
    if (str === num.toString() || str.includes('.')) {
      return num;
    }
  }

  // 否则返回清洗后的字符串
  return str;
};

// 🧹 清洗表头（统一使用智能清洗）
const cleanHeader = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/^['"]+|['"]+$/g, '');
};

/**
 * 解析文件，返回第一行表头数据（数组）—— 已清洗（纯字符串）
 */
export const getHeaderList = async (file) => {
  try {
    const isCSV = file.name.toLowerCase().endsWith('.csv');

    let workbook;

    if (isCSV) {
      const text = await decodeFile(file);
      workbook = XLSX.read(text, { type: 'string' });
    } else {
      const arrayBuffer = await readFileAsArrayBuffer(file);
      workbook = XLSX.read(new Uint8Array(arrayBuffer), {
        type: 'array',
        cellDates: true, // ✅ 保持一致
      });
    }

    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (!jsonData || jsonData.length === 0) {
      throw new Error('表格为空');
    }

    // 表头始终作为字符串处理（即使它看起来像数字），并过滤空值
    return jsonData[0].map(cleanHeader).filter(header => header !== '' && header != null);
  } catch (err) {
    throw new Error('文件解析失败：' + err.message);
  }
};

/**
 * 根据三个表头名称，获取对应列的所有原始数据（保留数字、时间、文字等类型）
 * 返回 { col1: [...], col2: [...], col3: [...] }，每个元素是智能清洗后的值
 * 数字 → number，文本 → string，null/undefined → ''
 */
export const getColumnDataByHeader = async (file, header1, header2, header3) => {
  try {
    const isCSV = file.name.toLowerCase().endsWith('.csv');

    let workbook;

    if (isCSV) {
      const text = await decodeFile(file);
      workbook = XLSX.read(text, { type: 'string' });
    } else {
      const arrayBuffer = await readFileAsArrayBuffer(file);
      workbook = XLSX.read(new Uint8Array(arrayBuffer), {
        type: 'array',
        cellDates: true, // ✅ 关键：让 Excel 日期转为 JS Date 对象
      });
    }

    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (!jsonData || jsonData.length === 0) {
      throw new Error('表格为空');
    }

    // ✅ 清洗表头用于查找（纯字符串）
    const headers = jsonData[0].map(cleanHeader);

    const idx1 = headers.indexOf(header1);
    const idx2 = headers.indexOf(header2);
    const idx3 = headers.indexOf(header3);

    if (idx1 === -1 || idx2 === -1 || idx3 === -1) {
      const missing = [];
      if (idx1 === -1) missing.push(header1);
      if (idx2 === -1) missing.push(header2);
      if (idx3 === -1) missing.push(header3);
      throw new Error(`未找到表头：${missing.join(', ')}`);
    }

    const col1 = [], col2 = [], col3 = [];

    // ✅ 对每一行的每个单元格进行智能清洗（保留数字类型）
    for (let row of jsonData.slice(1)) {
      col1.push(smartCleanCell(row[idx1]));
      col2.push(smartCleanCell(row[idx2]));
      col3.push(smartCleanCell(row[idx3]));
    }

    return { col1, col2, col3 };
  } catch (err) {
    throw new Error('文件解析失败：' + err.message);
  }
};

// 通用读取 ArrayBuffer
function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsArrayBuffer(file);
  });
}